import { jstDateTime } from '@/utils/jstDate';
import { toast } from 'sonner';

const fmt = (s) => (s ? jstDateTime(s) : '—');
const fmtDate = (s) => (s ? String(s).slice(0, 10) : '—');
const yen = (n) => (n == null ? '—' : `¥${Number(n).toLocaleString('ja-JP')}`);
const num = (n) => (n == null ? '—' : Number(n).toLocaleString('ja-JP'));

const copyPath = (path) => {
  if (!path) return;
  navigator.clipboard?.writeText(path)
    .then(() => toast.success('パスをコピーしました'))
    .catch(() => toast.error('コピーできませんでした'));
};

const Section = ({ no, title, testid, hint, children }) => (
  <section className="mt-5 break-inside-avoid" data-testid={testid}>
    <h2 className="border-b-2 border-zinc-800 pb-1 text-base font-bold text-zinc-900">
      {no}. {title}
    </h2>
    {hint && <p className="mt-0.5 text-[11px] text-zinc-500">{hint}</p>}
    <div className="mt-2">{children}</div>
  </section>
);

const Row = ({ label, value, testid }) => (
  <div className="flex gap-3 border-b border-zinc-200 py-1" data-testid={testid}>
    <dt className="w-36 shrink-0 text-xs font-semibold text-zinc-600">{label}</dt>
    <dd className="whitespace-pre-wrap text-sm text-zinc-900">{value || '—'}</dd>
  </div>
);

const TH = 'py-1 pr-2 text-left text-xs text-zinc-600';
const TD = 'py-1 pr-2 align-top';
const NUM = 'py-1 pr-2 text-right align-top tabular-nums';

const Figure = ({ label, value, hint, tone = 'text-zinc-900', testid }) => (
  <div className="rounded border border-zinc-300 px-2 py-1" data-testid={testid}>
    <p className="text-[10px] font-semibold text-zinc-600">{label}</p>
    <p className={`text-sm font-bold tabular-nums ${tone}`}>{value}</p>
    {hint && <p className="text-[10px] text-zinc-500">{hint}</p>}
  </div>
);

const docLine = (x) => [x.document_number, x.title, x.revision ? `版 ${x.revision}` : null]
  .filter(Boolean).join(' ');

const docWhere = (x) => (x.path
  ? x.path
  : (x.storage === 'external_path' ? '共有フォルダー（パス未登録）' : 'ドキュメントアプリ内'));

const StageBar = ({ stages }) => (
  <div className="flex flex-wrap items-center gap-1" data-testid="dev-handover-stage-bar">
    {(stages || []).map((s, i) => (
      <span key={s.id} className="flex items-center gap-1">
        {i > 0 && <span className="text-[10px] text-zinc-400">›</span>}
        <span data-testid={`dev-handover-stage-${s.id}`}
          className={`rounded px-1.5 py-0.5 text-[11px] ${s.current
            ? 'bg-zinc-900 font-bold text-white'
            : s.reached ? 'bg-zinc-200 text-zinc-700' : 'text-zinc-400'}`}>
          {s.label}
        </span>
      </span>
    ))}
  </div>
);

const CONCEPT_LABELS = [['background', '背景'], ['purpose', '目的'],
  ['intended_use', '想定する利用先'], ['target_spec', '目標仕様'],
  ['estimated_cost', '想定コスト'], ['current_problem', '現状の問題'],
  ['expected_effect', '期待する効果'], ['constraints', '制約条件'], ['notes', '備考']];

/** 旧い控えには concept_fields が無い。本文の `concept` から同じ並びで作る。 */
const conceptFieldsOf = (d) => d.concept_fields
  || CONCEPT_LABELS.map(([key, label]) => ({
    key, label, value: ((d.concept || {})[key] || '').trim() || null }));

const STAGE_LIST = [['concept', '構想'], ['parts_planning', '部品計画'],
  ['procurement', '調達'], ['prototype', '試作'], ['validation', '検証'],
  ['revision', '改訂'], ['completed', '完了']];

/** 旧い凍結記録（header を持たない版）でも「—」だらけにしない。 */
export const deriveHeader = (d) => {
  const s = d.summary || {};
  const ps = d.prototype_summary || {};
  const promo = d.promotion || {};
  const cur = s.current_stage || (d.completed_at ? 'completed' : 'concept');
  const idx = STAGE_LIST.findIndex(([k]) => k === cur);
  return {
    short_id: (s.id || '').split('-')[0] || null,
    owner_names: s.owner_names || [],
    started_on: s.created_at || null,
    target_completion_date: s.target_completion_date || null,
    completed_at: d.completed_at || null,
    is_overdue: s.is_overdue, overdue_days: s.overdue_days,
    is_completed: !!d.completed_at,
    state_label: d.completed_at
      ? `完了 → ${promo.no || promo.label || ''}`.trim()
      : `未完了 — 現在: ${s.current_stage_label || '—'}`,
    stage_bar: STAGE_LIST.map(([id, label], i) => ({
      id, label, current: id === cur, reached: idx >= 0 && i <= idx })),
    figures: {
      final_cost: null, per_unit_cost: null,
      bom_lines: (d.final_bom || []).length,
      prototypes: ps.prototypes ?? (d.prototypes || []).length,
      validations: ps.validations ?? (d.validations || []).length,
      validations_passed: (ps.passed ?? 0) + (ps.passed_with_issues ?? 0),
      validations_ng: (ps.failed ?? 0) + (ps.needs_revision ?? 0),
      failure_records: ps.failure_records ?? 0,
    },
    legacy: true,
  };
};

/** 1枚目に「どこまで進んで、いくらかかったか」をまとめる箱。 */
const HeaderBlock = ({ d, frozenAt }) => {
  const h = d.header || deriveHeader(d);
  const s = d.summary || {};
  const f = h.figures || {};
  const version = frozenAt
    ? `凍結記録（${fmtDate(frozenAt)}, ${d.generated_by || '—'}）`
    : 'ライブ（自動生成）';
  return (
    <div className="mt-3 rounded-md border-2 border-zinc-800" data-testid="dev-handover-header-block">
      <div className="flex items-start gap-3 border-b border-zinc-300 px-3 py-2">
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-semibold text-zinc-600">開発案件 完了報告書・引継ぎ資料</p>
          <h1 className="text-xl font-bold text-zinc-900">{s.title}</h1>
          <p className="mt-0.5 text-[11px] text-zinc-600">
            案件ID {h.short_id || '—'} ／ {s.development_type_label || '—'}
            {s.project_name ? ` ／ プロジェクト: ${s.project_name}` : ''}
          </p>
        </div>
        {/* 7 · 承認 / 確認 / 作成 の押印欄（14 × 14 mm・2026-11-04） */}
        <div className="flex shrink-0 gap-1" data-testid="dev-handover-stamps">
          {['承認', '確認', '作成'].map((label) => (
            <div key={label} className="text-center" data-testid={`dev-handover-stamp-${label}`}>
              <div className="border border-zinc-500 bg-white px-1 text-[8px] leading-tight text-zinc-600">
                {label}
              </div>
              <div className="border border-t-0 border-zinc-500"
                style={{ width: '14mm', height: '14mm' }} />
            </div>
          ))}
        </div>
      </div>
      <div className="grid gap-x-4 gap-y-1 px-3 py-2 sm:grid-cols-2">
        <dl>
          <Row label="対象部署" value={(s.target_department_names || []).join('、')} />
          <Row label="担当" value={h.lead_name || (h.owner_names || [])[0]}
            testid="dev-handover-lead" />
          <Row label="メンバー" value={(h.member_names || []).join('、')}
            testid="dev-handover-members" />
          <Row label="受入担当（製造側）" value={h.acceptance_name}
            testid="dev-handover-acceptance" />
          <Row label="製作台数" value={d.build_quantity ? `${d.build_quantity}台` : null} />
        </dl>
        <dl>
          <Row label="開始日" value={fmtDate(h.started_on)} testid="dev-handover-started" />
          <Row label="目標完了日" value={fmtDate(h.target_completion_date)} />
          {h.original_target_completion_date
            && h.original_target_completion_date !== h.target_completion_date && (
            <Row label="当初目標" value={fmtDate(h.original_target_completion_date)}
              testid="dev-handover-original-target" />
          )}
          <Row label="実際の完了日"
            value={h.completed_at ? fmtDate(h.completed_at) : '未完了'}
            testid="dev-handover-completed-on" />
        </dl>
      </div>
      {h.is_overdue && (
        <p className="px-3 pb-1 text-[12px] font-bold text-rose-700" data-testid="dev-handover-overdue">
          目標完了日を {h.overdue_days} 日超過しています
        </p>
      )}
      <div className="border-t border-zinc-300 px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-zinc-600">現在のステージ</span>
          <StageBar stages={h.stage_bar} />
        </div>
        <p className={`mt-1 text-[13px] font-bold ${h.is_completed ? 'text-teal-800' : 'text-amber-800'}`}
          data-testid="dev-handover-state">
          状態: {h.state_label}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-1.5 border-t border-zinc-300 px-3 py-2 sm:grid-cols-5"
        data-testid="dev-handover-figures">
        <Figure label={h.is_completed ? '最終原価' : '原価（計画 / 実績）'}
          value={h.is_completed || f.planned_cost == null
            ? yen(f.final_cost)
            : `${yen(f.planned_cost)} / ${yen(f.final_cost)}`}
          hint={h.is_completed
            ? (f.per_unit_cost != null ? `1台あたり ${yen(f.per_unit_cost)}` : null)
            : '試作に投入した分が実績です'}
          tone="text-teal-800" testid="dev-handover-fig-cost" />
        <Figure label="部品点数" value={num(f.bom_lines)} testid="dev-handover-fig-bom" />
        <Figure label="試作" value={`${num(f.prototypes)}件`} testid="dev-handover-fig-proto" />
        <Figure label="検証" value={`${num(f.validations)}件`}
          hint={`OK ${f.validations_passed ?? 0} / NG ${f.validations_ng ?? 0}`}
          testid="dev-handover-fig-val" />
        <Figure label="不具合" value={`${num(f.failure_records)}件`} tone="text-rose-700"
          testid="dev-handover-fig-failure" />
      </div>
      <p className="border-t border-zinc-300 px-3 py-1 text-[11px] text-zinc-600">
        版: {version} ／ 出力 {fmt(d.generated_at)} ／ {d.generated_by}
        {h.legacy ? ' ／ この版は古い形式のため、原価など一部の情報がありません' : ''}
      </p>
    </div>
  );
};

/**
 * 完了報告書・引継ぎ資料の本文。
 *
 * 画面（/engineering/developments/:id/handover）・印刷/PDF・設備カードに保存された
 * 凍結版が、すべてこの同じ部品を描く。章の並びは案件の流れ順
 * （なぜ → 部品と値段 → 作った → 試した → 図面・書類 → 何になったか → 引継ぎ）。
 */
/** 7 · 完了報告書の写真欄（最大6枚・1行2枚）。画像は呼び出し側が読み込んで渡す。 */
const ReportPhotos = ({ photos = [], testid = 'dev-handover-photos' }) => {
  if (!photos.length) return null;
  return (
    <div className="grid grid-cols-2 gap-2" data-testid={testid}>
      {photos.slice(0, 6).map((p) => (
        <figure key={p.document_id} className="break-inside-avoid"
          data-testid={`${testid}-${p.document_id}`}>
          <div className="h-40 w-full overflow-hidden border border-zinc-300 bg-zinc-100">
            {p.url && <img src={p.url} alt={p.title || '写真'}
              className="h-full w-full object-contain" />}
          </div>
          <figcaption className="mt-0.5 truncate text-[10px] text-zinc-600">
            {p.group ? `${p.group} · ` : ''}{p.title || '写真'}
          </figcaption>
        </figure>
      ))}
    </div>
  );
};

export const HandoverReport = ({ d, frozenAt = null, photos = [] }) => {
  const h = d.header || deriveHeader(d);
  const s = d.summary || {};
  const prs = d.prototype_summary || {};
  const costs = d.costs || {};
  const notes = d.handover_notes || {};
  const paths = d.path_links || [];
  const filled = conceptFieldsOf(d).filter((f) => f.value);
  const empty = conceptFieldsOf(d).filter((f) => !f.value);
  const docRows = (d.checklist || []).filter((c) => (c.documents || []).length > 0);
  const stageRows = (d.stage_logs || []);
  // 検証の件数は検証の行の判定で数える（古い控えは本文の行から数える）
  const vals = d.validations || [];
  const vc = d.validation_counts || {
    total: vals.length,
    passed: vals.filter((v) => v.result === 'pass').length,
    passed_with_issues: vals.filter((v) => v.result === 'pass_with_issues').length,
    failed: vals.filter((v) => v.result === 'fail').length,
    needs_revision: vals.filter((v) => v.result === 'needs_revision').length };
  // 旧い控えには estimated が無い。本文の 想定コスト から画面側でも読む。
  const est = d.estimated || {
    text: conceptFieldsOf(d).find((f) => f.key === 'estimated_cost')?.value || null,
    parsed: false, value: null, actual: costs.final_cost ?? null,
    variance: null, variance_pct: null, over_budget: false, note: null };

  return (
    <div data-testid="dev-handover-report">
      {/* 印刷のとき、どのページにも小さく見出しを出す */}
      <div className="hidden print:fixed print:left-0 print:right-0 print:top-0 print:block
        print:border-b print:border-zinc-300 print:bg-white print:px-2 print:py-0.5
        print:text-[9px] print:text-zinc-600" data-testid="dev-handover-running-head">
        {s.title} ／ {frozenAt ? `凍結記録 ${fmtDate(frozenAt)}` : 'ライブ'} ／ 出力 {fmtDate(d.generated_at)}
      </div>

      <HeaderBlock d={d} frozenAt={frozenAt} />

      <Section no={1} title="構想提案（なぜ・何をつくるか）" testid="dev-handover-concept">
        <dl>
          {filled.map((f) => (
            <Row key={f.key} label={f.label} value={f.value} testid={`dev-handover-concept-${f.key}`} />
          ))}
          {[['種類', s.development_type_label],
            ['対象部署', (s.target_department_names || []).join('、')],
            ['担当', h.lead_name || (h.owner_names || [])[0]],
            ['メンバー', (h.member_names || []).join('、')],
            ['受入担当（製造側）', h.acceptance_name],
            ['製作台数', d.build_quantity ? `${d.build_quantity}台` : '']]
            .filter(([, v]) => v).map(([lb, v]) => <Row key={lb} label={lb} value={v} />)}
        </dl>
        {empty.length > 0 && (
          <p className="mt-1 text-[11px] text-zinc-500" data-testid="dev-handover-concept-empty">
            未記入: {empty.map((f) => f.label).join('、')}
          </p>
        )}
      </Section>

      <Section no={2} title="部品構成と原価" testid="dev-handover-bom">
        {(d.final_bom || []).length === 0 ? <p className="text-sm text-zinc-500">部品は登録されていません。</p> : (
          <table className="w-full border-collapse text-sm">
            <thead><tr className="border-b-2 border-zinc-400">
              <th className={TH}>No</th><th className={TH}>品番 / SKU</th><th className={TH}>品名</th>
              <th className={TH}>区分</th><th className={TH}>図番 / 版</th>
              <th className={`${TH} text-right`}>1台</th><th className={`${TH} text-right`}>必要数</th>
              <th className={`${TH} text-right`}>単価</th><th className={`${TH} text-right`}>金額</th>
              <th className={TH}>手配</th><th className={TH}>在庫 / 状態</th>
            </tr></thead>
            <tbody>
              {d.final_bom.map((p, i) => (
                <tr key={p.id || `${p.name}-${i}`} className="break-inside-avoid border-b border-zinc-200"
                  data-testid={`dev-handover-bom-row-${i}`}>
                  <td className={`${TD} text-xs text-zinc-500`}>{i + 1}</td>
                  <td className={`${TD} font-mono text-[11px]`}>{p.part_no || '—'}</td>
                  <td className={TD}>{p.name}{p.model_no ? ` · ${p.model_no}` : ''}</td>
                  <td className={`${TD} text-xs`}>{p.make_type_label || p.source_label}</td>
                  <td className={`${TD} text-xs`}>
                    {p.drawing_no || '—'}{p.drawing_revision ? ` ${p.drawing_revision}` : ''}
                  </td>
                  <td className={NUM}>{p.qty_per_unit}</td>
                  <td className={NUM}>{p.required_total}{p.uom}</td>
                  <td className={NUM}>{p.unit_cost_known ? yen(p.unit_cost) : '—'}</td>
                  <td className={NUM}>{p.unit_cost_known ? yen(p.amount) : '—'}</td>
                  <td className={`${TD} text-xs`}>
                    {[...(p.requests || []).map((r) => `${r.request_no || 'REQ'} ${r.status_label || ''}`),
                      ...(p.fabrications || []).map((x) => `${x.fab_no || 'FAB'} ${x.status_label || ''}`)]
                      .join(' / ') || p.flow_status_label || '—'}
                  </td>
                  <td className={`${TD} text-xs`}>
                    {p.available_known ? `在庫 ${p.available}` : '在庫不明'} · {p.status_label}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-zinc-400 font-bold" data-testid="dev-handover-bom-total">
                <td className={TD} colSpan={8}>合計（{d.final_bom.length}点）</td>
                <td className={NUM}>{yen(d.bom_total_amount)}</td>
                <td className={TD} colSpan={2} />
              </tr>
            </tbody>
          </table>
        )}
        <div className="mt-2 break-inside-avoid rounded border border-zinc-300 p-2"
          data-testid="dev-handover-costs">
          <p className="text-xs font-bold text-zinc-800">原価の積み上げ</p>
          <dl className="mt-1">
            <Row label="材料費（計画）" value={yen(costs.material_cost)} />
            <Row label="試作費（実際に投入）" value={yen(costs.prototype_cost)} />
            <Row label="スクラップ費"
              value={costs.scrap_rows > 0 && !costs.scrap_cost_known && costs.scrap_cost === 0
                ? '不明' : yen(costs.scrap_cost)} />
            <Row label="在庫へ返却（差引）" value={yen(costs.returned_credit)} />
            <Row label="最終原価" value={yen(costs.final_cost)} testid="dev-handover-final-cost" />
            <Row label="1台あたり" value={yen(costs.per_unit_cost)} />
          </dl>
          {/* 想定 / 実績 / 差額（想定コストが数値として読めたときだけ計算する） */}
          <div className="mt-2 border-t border-zinc-200 pt-1" data-testid="dev-handover-variance">
            <dl>
              <Row label="想定コスト（提案）"
                value={est.parsed ? yen(est.value) : (est.text || '未記入')}
                testid="dev-handover-estimated" />
              <Row label="実績（最終原価）" value={yen(est.actual ?? costs.final_cost)} />
            </dl>
            {est.parsed ? (
              <p className={`mt-1 text-[13px] font-bold ${est.over_budget ? 'text-rose-700' : 'text-teal-800'}`}
                data-testid="dev-handover-variance-value">
                差額: {est.variance > 0 ? '+' : ''}{yen(est.variance)}
                {est.variance_pct == null ? '' : `（${est.variance_pct > 0 ? '+' : ''}${est.variance_pct}%）`}
                {est.over_budget ? ' — 想定を超えています' : ' — 想定内'}
              </p>
            ) : est.note ? (
              <p className="mt-1 text-[11px] text-amber-800" data-testid="dev-handover-variance-note">
                {est.note}
              </p>
            ) : null}
          </div>
          {costs.note && <p className="mt-1 text-[11px] text-amber-800">{costs.note}</p>}
        </div>
      </Section>

      <Section no={3} title="試作の履歴" testid="dev-handover-prototypes">
        {(d.prototypes || []).length === 0 ? <p className="text-sm text-zinc-500">試作なし。</p> : (
          <table className="w-full border-collapse text-sm">
            <thead><tr className="border-b-2 border-zinc-400">
              <th className={TH}>試作</th><th className={TH}>製作日</th><th className={TH}>担当</th>
              <th className={`${TH} text-right`}>台数</th><th className={TH}>投入部品</th>
              <th className={TH}>結果 / メモ</th>
            </tr></thead>
            <tbody>
              {d.prototypes.map((p) => {
                const lines = p.installed_parts || [];
                const amount = (costs.prototype_lines || [])
                  .filter((x) => x.prototype_id === p.id)
                  .reduce((a, x) => a + Number(x.amount || 0), 0);
                return (
                  <tr key={p.id} className="break-inside-avoid border-b border-zinc-200"
                    data-testid={`dev-handover-proto-${p.id}`}>
                    <td className={TD}>{p.name}{p.cancelled ? '（取消）' : ''}</td>
                    <td className={`${TD} text-xs`}>{fmtDate(p.build_date)}</td>
                    <td className={`${TD} text-xs`}>{(p.builder_names || []).join('、') || '—'}</td>
                    <td className={NUM}>{p.build_quantity}</td>
                    <td className={`${TD} text-xs`}>
                      {lines.length}点 / {yen(amount)}
                      <span className="block text-zinc-500">
                        返却 {p.part_counts?.returned} · 手直し {p.part_counts?.rework} ·
                        スクラップ {p.part_counts?.scrapped}
                      </span>
                    </td>
                    <td className={`${TD} text-xs`}>
                      {p.latest_result_label || p.status_label}
                      {p.notes ? <span className="block text-zinc-600">{p.notes}</span> : null}
                      {p.cancel_reason_label
                        ? <span className="block text-zinc-600">取消: {p.cancel_reason_label}</span> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Section>

      <Section no={4} title="検証の履歴と不具合" testid="dev-handover-validations">
        <p className="text-[12px] text-zinc-700" data-testid="dev-handover-validation-counts">
          検証 {vc.total ?? 0}件（合格 {vc.passed ?? 0} / 限定合格 {vc.passed_with_issues ?? 0} /
          不合格 {vc.failed ?? 0} / 要改訂 {vc.needs_revision ?? 0}） ／
          不具合 {prs.failure_records ?? 0}件（再発 {prs.recurrences ?? 0}）
        </p>
        {(d.validations || []).length === 0 ? <p className="mt-1 text-sm text-zinc-500">検証記録はありません。</p> : (
          <table className="mt-1 w-full border-collapse text-sm">
            <thead><tr className="border-b-2 border-zinc-400">
              <th className={TH}>実施日</th><th className={TH}>試作</th><th className={TH}>項目</th>
              <th className={TH}>判定</th><th className={TH}>測定値 / 条件</th>
              <th className={TH}>不具合 → 対策 → 再発</th>
            </tr></thead>
            <tbody>
              {d.validations.map((v) => (
                <tr key={v.id} className="break-inside-avoid border-b border-zinc-200">
                  <td className={`${TD} text-xs`}>{fmtDate(v.tested_at)}</td>
                  <td className={`${TD} text-xs`}>{v.prototype_name}</td>
                  <td className={TD}>{v.tested_item}</td>
                  <td className={`${TD} text-xs font-semibold`}>{v.result_label}</td>
                  <td className={`${TD} text-xs`}>{v.notes || '—'}</td>
                  <td className={`${TD} text-xs`}>
                    {[v.issue_description, v.cause, v.countermeasure].filter(Boolean).join(' → ') || '—'}
                    {v.failure_category_label
                      ? <span className="block text-zinc-500">分類: {v.failure_category_label}</span> : null}
                    {v.recurrence ? <span className="block font-semibold text-rose-700">再発</span> : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <Section no={5} title="図面リビジョン履歴" testid="dev-handover-revisions">
        {(d.revisions || []).length === 0 ? <p className="text-sm text-zinc-500">図番のある部品はありません。</p> : (
          <table className="w-full border-collapse text-sm">
            <thead><tr className="border-b-2 border-zinc-400">
              <th className={TH}>図番 / 部品</th><th className={TH}>版</th><th className={TH}>改訂日</th>
              <th className={TH}>改訂理由</th><th className={TH}>使用した試作と結果</th>
            </tr></thead>
            <tbody>
              {(d.revisions || []).flatMap((r) => (r.versions || []).map((v, i) => (
                <tr key={`${r.part_id}-${i}`} className="break-inside-avoid border-b border-zinc-200">
                  <td className={`${TD} text-xs`}>
                    {i === 0 ? `${r.drawing_no || '図番なし'} / ${r.part_name}` : ''}
                  </td>
                  <td className={`${TD} text-xs font-semibold`}>
                    {v.revision || '版なし'}{v.current ? '（現在）' : ''}
                  </td>
                  <td className={`${TD} text-xs`}>{fmtDate(v.changed_at)}</td>
                  <td className={`${TD} text-xs`}>{v.note || '—'}</td>
                  <td className={`${TD} text-xs`}>
                    {(v.used_in || []).length === 0 ? '—'
                      : v.used_in.map((u) => `${u.name}: ${u.result_label || u.status_label}`).join('、')}
                  </td>
                </tr>
              )))}
            </tbody>
          </table>
        )}
      </Section>

      <Section no={6} title="引き継ぐ書類と共有フォルダー（正本はドキュメントアプリ）"
        testid="dev-handover-documents">
        {/* 紙に印刷しても辿れるように、番号・件名・置き場所をそのまま書き出す */}
        <table className="w-full border-collapse text-sm">
          <thead><tr className="border-b-2 border-zinc-400">
            <th className={TH}>区分</th><th className={TH}>番号 / 件名</th><th className={TH}>どこにあるか</th>
          </tr></thead>
          <tbody>
            {docRows.flatMap((c) => c.documents.map((x, i) => (
              <tr key={`${c.key}-${x.document_id}`} className="break-inside-avoid border-b border-zinc-200"
                data-testid={`dev-handover-doc-${x.document_id}`}>
                <td className={`${TD} text-xs`}>{i === 0 ? c.label : ''}</td>
                <td className={TD}>{docLine(x)}</td>
                <td className="break-all py-1 align-top font-mono text-[11px] text-zinc-700">{docWhere(x)}</td>
              </tr>
            )))}
            {(d.unassigned_documents || []).map((x) => (
              <tr key={x.document_id} className="break-inside-avoid border-b border-zinc-200"
                data-testid={`dev-handover-doc-${x.document_id}`}>
                <td className={`${TD} text-xs`}>その他</td>
                <td className={TD}>{docLine(x)}</td>
                <td className="break-all py-1 align-top font-mono text-[11px] text-zinc-700">{docWhere(x)}</td>
              </tr>
            ))}
            {paths.map((p, i) => (
              <tr key={`${p.path}-${i}`} className="break-inside-avoid border-b border-zinc-200"
                data-testid={`dev-handover-path-${i}`}>
                <td className={`${TD} text-xs`}>{p.kind_label || '共有フォルダー'}</td>
                <td className={TD}>
                  {p.display_name || '（名前なし）'}
                  {p.note ? <span className="block text-[11px] text-zinc-500">{p.note}</span> : null}
                </td>
                <td className="py-1 align-top">
                  {/* 画面では押すとコピー、紙にはパスがそのまま出る */}
                  <button type="button" onClick={() => copyPath(p.path)} title="クリックでコピー"
                    className="break-all text-left font-mono text-[11px] text-zinc-700 hover:text-teal-700"
                    data-testid={`dev-handover-path-copy-${i}`}>
                    {p.path}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {docRows.length === 0 && (d.unassigned_documents || []).length === 0 && paths.length === 0 && (
          <p className="text-sm text-zinc-500">紐づいた書類・共有フォルダーはありません。</p>
        )}
        {(d.external_refs || []).length > 0 && (
          <p className="mt-2 text-sm">
            <span className="font-semibold">外部リソース:</span>{' '}
            {d.external_refs.map((r) => `${r.label || ''} ${r.url || ''}`.trim()).join('、')}
          </p>
        )}
      </Section>

      <Section no={7} title="結論（この開発が何になったか）" testid="dev-handover-outcome">
        {h.is_completed ? (
          <dl>
            <Row label="最終アウトカム" value={d.final_outcome_label} testid="dev-handover-outcome-label" />
            <Row label="登録先"
              value={(() => {
                const lb = d.promotion?.label || '';
                const no = d.promotion?.no || '';
                return no && !lb.includes(no) ? `${no} ${lb}`.trim() : (lb || no);
              })()}
              testid="dev-handover-promotion" />
            <Row label="登録日時" value={fmt(d.promotion?.promoted_at || d.completed_at)} />
            <Row label="登録者" value={d.promotion?.promoted_by_name} />
            <Row label="完了メモ" value={d.completion_notes} />
            {d.completion_reason && <Row label="理由（強制）" value={d.completion_reason} />}
          </dl>
        ) : (
          <div data-testid="dev-handover-not-completed">
            <p className="text-[13px] font-bold text-amber-800">{h.state_label}</p>
            {(d.completion_unmet || []).length > 0 && (
              <>
                <p className="mt-1 text-xs font-semibold text-zinc-700">完了に足りていない条件</p>
                <ul className="mt-0.5 space-y-0.5 text-[12px] text-zinc-900">
                  {d.completion_unmet.map((g, i) => (
                    <li key={g.label || i} data-testid={`dev-handover-gate-${i}`}>
                      ・{g.label}{g.detail ? `（${g.detail}）` : ''}
                    </li>
                  ))}
                </ul>
              </>
            )}
            {d.next_action && (
              <p className="mt-1 text-[12px] text-zinc-900" data-testid="dev-handover-next-action">
                次にやること: {String(d.next_action).replace(/^次:\s*/, '')}
              </p>
            )}
          </div>
        )}
      </Section>

      <Section no={8} title="引継ぎ事項" testid="dev-handover-notes">
        {/* 7 · 完了時に引継ぎメモが空なら、印刷物でも知らせる（止めない） */}
        {!notes.memo && (
          <p className="mb-1 border border-amber-400 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-900"
            data-testid="dev-handover-memo-missing">
            引継ぎメモが未入力です（次の担当に渡す内容を書いてください）
          </p>
        )}
        {/* 担当が書いたメモを先に、自動で拾った項目をあとに */}
        <div className="border-b-2 border-zinc-300 pb-1" data-testid="dev-handover-memo">
          <p className="text-xs font-semibold text-zinc-600">引継ぎメモ（次の担当へ）</p>
          <p className="whitespace-pre-wrap text-sm text-zinc-900">
            {notes.memo || 'なし'}
          </p>
          {notes.memo && notes.memo_updated_at && (
            <p className="text-[10px] text-zinc-500">
              {fmtDate(notes.memo_updated_at)}
              {notes.memo_updated_by_name ? ` · ${notes.memo_updated_by_name}` : ''}
            </p>
          )}
        </div>
        <dl className="mt-1">
          <Row label="未解決の課題・リスク" testid="dev-handover-open-issues"
            value={(notes.open_issues || []).length === 0 ? 'なし'
              : notes.open_issues.map((x) => `・${x.label}${x.detail ? `（${x.detail}）` : ''}`).join('\n')} />
          <Row label="保守・点検の注意"
            value={(notes.maintenance || []).join('\n') || 'なし'} />
          <Row label="予備部品の要点"
            value={(notes.spare_parts || []).length === 0 ? 'なし'
              : notes.spare_parts.map((x) => `${x.part_no ? `${x.part_no} ` : ''}${x.name} ${x.qty ?? ''}${x.uom || ''}`).join('\n')} />
          <Row label="次の担当" value={(notes.next_owner_names || []).join('、') || 'なし'} />
        </dl>
      </Section>

      <Section no={9} title="写真（案件・試作）" testid="dev-handover-photo-section">
        <ReportPhotos photos={photos} />
      </Section>


      <Section no="付録" title="ステージ履歴" testid="dev-handover-history">
        <table className="w-full border-collapse text-sm">
          <thead><tr className="border-b-2 border-zinc-400">
            <th className={TH}>日時</th><th className={TH}>From → To</th>
            <th className={`${TH} text-right`}>滞在日数</th><th className={TH}>実施者</th>
            <th className={TH}>備考</th>
          </tr></thead>
          <tbody>
            {stageRows.map((l, i) => (
              <tr key={l.id || i} className="break-inside-avoid border-b border-zinc-200"
                data-testid={`dev-handover-stage-row-${i}`}>
                <td className={`${TD} text-xs`}>{fmt(l.performed_at)}</td>
                <td className={`${TD} text-xs`}>
                  {l.noop ? `${l.to_stage_label}（再提出）`
                    : `${l.from_stage_label || '—'} → ${l.to_stage_label}`}
                </td>
                <td className={NUM}>{l.days_in_stage == null ? '—' : `${l.days_in_stage}日`}</td>
                <td className={`${TD} text-xs`}>{l.performed_by_name}</td>
                <td className={`${TD} text-xs`}>
                  {l.forced ? '強制' : ''}{l.reason ? ` ${l.reason}` : ''}
                </td>
              </tr>
            ))}
            {stageRows.length === 0 && (
              <tr><td className="py-1 text-sm text-zinc-500" colSpan={5}>履歴はありません。</td></tr>
            )}
          </tbody>
        </table>
      </Section>

      <p className="mt-6 text-xs text-zinc-500">
        この資料は開発案件の記録から自動生成されています。書類・図面は複製せず、ドキュメントアプリの正本を参照してください。
      </p>
    </div>
  );
};

export default HandoverReport;
